import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import React, { Dispatch, FC, SetStateAction, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import Button from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import NavigationSection from '@ui/atoms/NavigationSection';
import { IButtonProps } from '@ui/atoms/Ws-Button';

import PasswordValidationInput from '@ui/forms/PasswordValidationInput';

import SubmitButton, { ISubmitButtonProps } from '@ui/molecules/SubmitButton';

import fullWidth from '@ui/styled/FullWidth';

import { keysToCamel } from '@utils/butter';
import { ApiEndpoints } from '@utils/endpoints';

import { useAuth } from '@providers/AuthProvider';

interface IMembershipSetPasswordStepContent {
	title: string;
	passwordLabel: string;
}

interface IMembershipSetPasswordStepProps {
	stepKey: string;
	registrationCode: string;
	setIsSmsMfa: Dispatch<SetStateAction<boolean>>;
	setCredentials: Dispatch<SetStateAction<{ email: string; password: string }>>;
	handleNavBack: (currentStepKey: string) => void;
	onComplete: () => void;
	destinationFlow: string;
}

const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const FullWidthForm = fullWidth('form');
const FullWidthButton = fullWidth<IButtonProps>(Button);
const FullWidthSubmitButton = fullWidth<ISubmitButtonProps>(SubmitButton);

const MembershipSetPasswordStep: FC<IMembershipSetPasswordStepProps> = ({
	registrationCode,
	stepKey,
	setCredentials,
	setIsSmsMfa,
	handleNavBack,
	onComplete,
	destinationFlow,
}) => {
	const [isOnboarding, setIsOnboarding] = useState(false);

	const hasUppercase = /[A-Z]/;
	const hasLowercase = /[a-z]/;
	const hasDigit = /[0-9]/;

	const { confirmPassword } = useAuth();

	const { pageContent } = useStaticQuery<Record<string, string>>(
		graphql`
			{
				pageContent: butterPage(slug: { eq: "membership-set-password" }) {
					title
					password_label
				}
			}
		`
	);

	const { title, passwordLabel } = keysToCamel(pageContent) as IMembershipSetPasswordStepContent;

	const schema = z.object({
		password: z.string().regex(hasUppercase).regex(hasLowercase).regex(hasDigit).min(10),
	});

	type Schema = Partial<z.input<typeof schema>>;

	const { handleSubmit, control } = useForm<Schema>({
		resolver: zodResolver(schema),
		mode: 'onChange',
	});

	const handleOnboardingConfirmation: SubmitHandler<Schema> = async (data: Schema) => {
		setIsOnboarding(true);
		try {
			const { password } = data;
			const response = await axios.post(ApiEndpoints.memberOnboardingConfirmation, {
				registration_code: registrationCode,
			});
			const { tempPwd: tempPassword, email } = response.data;
			setCredentials({ email, password: password ?? '' });
			await confirmPassword(email, tempPassword, password ?? '');
			onComplete();
		} catch (e: any) {
			console.log(e);
			if (e?.code === 'UserLambdaValidationException') {
				setIsSmsMfa(true);
				return;
			}

			setIsOnboarding(false);
			console.error(`Error occurred: ${JSON.stringify(e)}`);
			navigate(`/app/flows/${destinationFlow}/eligibility`, {
				state: {
					error: `We ran into an error processing your request. Please try again.`,
				},
			});
		}
	};
	return (
		<FullWidthForm onSubmit={handleSubmit(handleOnboardingConfirmation)}>
			<FullWidthColumn gap="lg">
				<HeadingText>{title}</HeadingText>
				<FlexColumn>
					<Controller
						name="password"
						control={control}
						render={({ field: { value, onChange, ...field } }) => (
							<PasswordValidationInput
								label={passwordLabel}
								hasValidLength={(value?.length ?? 0) >= 10}
								validLengthLabel="10 or more characters"
								hasUppercaseLetter={(value ?? '').match(hasUppercase) !== null}
								validUppercaseLabel="Upper case letter"
								hasLowercaseLetter={(value ?? '').match(hasLowercase) !== null}
								validLowercaseLabel="Lower case letter"
								hasNumber={(value ?? '').match(hasDigit) !== null}
								validNumberLabel="At least 1 number"
								onChange={onChange}
								borderColor="black"
								{...field}
							/>
						)}
					/>
				</FlexColumn>
				<NavigationSection>
					<FullWidthButton
						label="Back"
						type="button"
						dataCy="Back"
						onClick={() => handleNavBack(stepKey)}
					/>
					<FullWidthSubmitButton
						label="Next"
						variant="secondary"
						submitting={isOnboarding}
						dataCy="Next"
					/>
				</NavigationSection>
			</FullWidthColumn>
		</FullWidthForm>
	);
};

export default MembershipSetPasswordStep;
